import { Component, DestroyRef, OnInit, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Keycloak from 'keycloak-js';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { ApiService } from '@core/services/api';
import { UserEnvService } from '@core/services/user-env/user-env.service';
import { isAppOrTokenRoleAdmin } from '@core/utils/keycloak-app-admin';
import { SharedModule } from '@shared/shared.module';

/** Preference scope: user list vs role list. */
export type KlDesignPreferenceLevelType = 'user' | 'role';

/** Same payload the existing preference save handlers expect. */
export interface KlDesignPreferenceResult {
  entryName: string;
  commitType: 'add' | 'edit';
  level: string;
  levelDesign: boolean;
  levelValues: string[];
}

@Component({
  selector: 'kl-design-preference-dynamic',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './kl-design-preference-dynamic.component.html',
  styleUrl: './kl-design-preference-dynamic.component.scss',
})
export class KlDesignPreferenceDynamicComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly keycloak = inject(Keycloak);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly userEnv = inject(UserEnvService);
  private readonly destroyRef = inject(DestroyRef);

  readonly commitType = input.required<'add' | 'edit'>();
  readonly currentData = input<Record<string, unknown> | null>(null);
  /** i18n key for the name field label (e.g. `filter_name`, `design_name`). */
  readonly nameLabelKey = input<string>('design_name');
  /** When set, runs before close and keeps the dialog open with a loading state until it completes. */
  readonly submitAction = input<(result: KlDesignPreferenceResult) => Observable<unknown>>();

  entryName = '';
  levelDesign = false;
  levelType: KlDesignPreferenceLevelType = 'user';
  selectedUsers: Record<string, unknown>[] = [];
  selectedRoles: Record<string, unknown>[] = [];

  readonly userList = signal<Record<string, unknown>[]>([]);
  readonly roleList = signal<Record<string, unknown>[]>([]);
  readonly loadingMeta = signal(false);
  readonly saving = signal(false);
  readonly showRoleAndUsers = signal(false);
  errorShow = false;

  ngOnInit(): void {
    this.userEnv.loadFromSession();
    const tokenParsed = this.keycloak.tokenParsed;
    const env = this.userEnv.env();

    const applyAdminAndLoad = (profile?: Record<string, unknown> | null) => {
      this.showRoleAndUsers.set(isAppOrTokenRoleAdmin(tokenParsed, env, profile));
      this.loadDialogData();
    };

    if (isAppOrTokenRoleAdmin(tokenParsed, env)) {
      applyAdminAndLoad();
      return;
    }

    this.api
      .getAllData<Record<string, unknown>>('user/my')
      .pipe(catchError(() => of(null)))
      .subscribe((me) => applyAdminAndLoad(me));
  }

  cancel(): void {
    this.dialogRef.close();
  }

  /**
   * Validate the form and close the dialog with a {@link KlDesignPreferenceResult}.
   *
   * When {@link submitAction} is provided (e.g. grid design save), the dialog stays
   * open with `[loading]` on Save until the observable completes; only then does it
   * close with the result. Without `submitAction`, closes immediately—callers
   * handle persistence in the dialog `onClose` subscription.
   */
  confirm(): void {
    const result = this.buildResult();
    if (!result) {
      this.errorShow = true;
      return;
    }

    const action = this.submitAction();
    if (action) {
      this.saving.set(true);
      action(result)
        .pipe(
          finalize(() => this.saving.set(false)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: () => this.dialogRef.close(result),
          error: () => {},
        });
      return;
    }

    this.dialogRef.close(result);
  }

  /**
   * Map dialog fields to the preference payload (`level`, `level_values`, etc.).
   *
   * Admins ({@link showRoleAndUsers}) choose USER / USER_ALL / ROLE / ROLE_ALL from
   * multi-selects. Non-admins always save at USER scope for the current app user (`user_id`).
   * Returns null when the name is empty (shows validation error).
   */
  private buildResult(): KlDesignPreferenceResult | null {
    const name = this.entryName.trim();
    if (!name) {
      return null;
    }

    let level: string;
    let levelValues: string[] = [];

    if (this.showRoleAndUsers()) {
      if (this.levelType === 'user') {
        if (this.selectedUsers.length > 0) {
          level = 'USER';
          levelValues = this.selectedUsers.map((u) => String(u['id'] ?? ''));
        } else {
          level = 'USER_ALL';
          levelValues = [];
        }
      } else if (this.selectedRoles.length > 0) {
        level = 'ROLE';
        levelValues = this.selectedRoles.map((r) => String(r['id'] ?? ''));
      } else {
        level = 'ROLE_ALL';
        levelValues = [];
      }
    } else {
      const userId = this.userEnv.env()?.user_id;
      level = 'USER';
      levelValues = userId != null ? [String(userId)] : [];
    }

    return {
      entryName: name,
      commitType: this.commitType(),
      level,
      levelDesign: this.levelDesign,
      levelValues,
    };
  }

  /**
   * Initialize or edit mode: load lookup lists and patch fields from {@link currentData}.
   *
   * Non-admins skip the user/role API calls and only {@link patchFromCurrent}. Admins
   * fetch branch/center users and app roles in parallel, then pre-select entries when
   * `commitType` is `edit`.
   */
  private loadDialogData(): void {
    this.errorShow = false;
    const cur = this.currentData();

    if (!this.showRoleAndUsers()) {
      this.patchFromCurrent(cur);
      return;
    }

    this.loadingMeta.set(true);
    forkJoin({
      users: this.api
        .getFilteredData<Record<string, unknown>>('user', {
          filter: { user_types: ['BRANCH_USER', 'CENTER_USER'] },
        })
        .pipe(catchError(() => of({ data: [] }))),
      roles: this.api
        .getFilteredData<Record<string, unknown>>('role', {
          filter: { role_type: 'APP_USER' },
        })
        .pipe(catchError(() => of({ data: [] }))),
    })
      .pipe(finalize(() => this.loadingMeta.set(false)))
      .subscribe({
        next: ({ users, roles }) => {
          this.userList.set((users?.data as Record<string, unknown>[]) ?? []);
          this.roleList.set((roles?.data as Record<string, unknown>[]) ?? []);
          this.patchFromCurrent(cur);
        },
      });
  }

  /**
   * Pre-fill dialog fields when editing an existing design/filter record.
   *
   * Maps API fields (`description`, `level`, `level_values`, `level_default`) onto
   * local form state. USER / USER_ALL switches to the user tab; ROLE / ROLE_ALL to
   * the role tab and resolves multi-select ids against {@link userList} /
   * {@link roleList}. Empty or missing `currentData` resets to add-mode defaults.
   */
  private patchFromCurrent(cur: Record<string, unknown> | null): void {
    if (cur && Object.keys(cur).length > 0) {
      const level = String(cur['level'] ?? 'USER');
      const levelValues = (cur['level_values'] as string[] | undefined) ?? [];
      this.entryName = String(cur['description'] ?? '');
      this.levelDesign = String(cur['level_default'] ?? '') === 'DEFAULT';

      if (level === 'USER' || level === 'USER_ALL') {
        this.levelType = 'user';
        if (level === 'USER' && levelValues.length > 0) {
          const users = this.userList();
          this.selectedUsers = users.filter((u) => levelValues.includes(String(u['id'] ?? '')));
        } else {
          this.selectedUsers = [];
        }
        this.selectedRoles = [];
        return;
      }

      this.levelType = 'role';
      const roles = this.roleList();
      this.selectedRoles = roles.filter((r) => levelValues.includes(String(r['id'] ?? '')));
      this.selectedUsers = [];
      return;
    }

    this.entryName = '';
    this.levelDesign = false;
    this.levelType = 'user';
    this.selectedUsers = [];
    this.selectedRoles = [];
  }
}
